
import { useAuth0 } from '@auth0/auth0-react';
import { Divider, Grid, Icon, IconButton } from "@mui/material";
import PropTypes from "prop-types";
import { useEffect } from "react";


import {
    useSoftUIController
} from "context";

import { useQuery } from '@tanstack/react-query';
import { setDeviceStyle, setHiddenSidenav, setMiniSidenav } from "context";


import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import WeatherProvider from "nextus/Weather";

import SoftButton from "components/SoftButton";
import moment from "moment";
import UptimeDoughnutChart from "nextus/Charts/StatisticCards/UptimeDoughnutChart";
import { useLocation, useNavigate } from "react-router-dom";
import SidebarRoutes from "../NavRoutes";
import { customSidebarStyle, sidebarHeadersStyle, sidebarTabStyle } from "../styles/index";

import deviceAPI from "api/bks";
import userAPI from 'api/user';
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import { processDeviceData } from "helper/device";
import { getUserAccessLevel } from "helper/user";

function timeOfDayMessage() {
    var time_of_day = moment().format("H");

    if (time_of_day < 12) {
        return 'Good Morning';
    }
    else if (time_of_day < 17) {
        return 'Good Afternoon';
    }
    else if (time_of_day <= 23) {
        return 'Good Evening';
    }

    return 'Good Day';
}

function getGroupData(devices_data, show_inactive = false) {

    var all_devices = devices_data.map(function (device) {
        return processDeviceData(device);
    })

    if (!show_inactive) {
        all_devices = all_devices.filter(row => row.active);
    }

    var data = devices_data;
    data.showing_all = true;
    data.devices = all_devices;
    data.name = "All Devices";

    return data;
}

function wrapper(content) {
    return (
        <SoftBox p={3}
            sx={({ palette: { white }, boxShadows: { navbarBoxShadow }, borders: { borderRadius }, functions: { rgba, pxToRem } }) => ({
                boxShadow: navbarBoxShadow,
                backdropFilter: `saturate(200%) blur(${pxToRem(4)})`,
                borderRadius: borderRadius.xl,
                height: 'calc(100% - 50px)',
                overflowY: 'auto',
            })}
        >
            {content}
        </SoftBox>
    )
}

function SideNav({ sidebarOpen = true, sidebarState }) {
    const { user } = useAuth0();
    const [controller, dispatch] = useSoftUIController();
    const { miniSidenav, hiddenSidenav, transparentSidenav, transparentInfo, deviceStyle } = controller;
    const location = useLocation();
    const { pathname } = location;
    const navigate = useNavigate();

    const closeSidenav = () => setMiniSidenav(dispatch, true);

    var transformX = 0;
    deviceAPI.loadToken(useAuth0());
    userAPI.loadToken(useAuth0());

    const devices = useQuery({
        queryKey: ['getAllDevices'],
        queryFn: async () => {
            return await deviceAPI.getAllDevices();
        },
        staleTime: 20 * 1000
    });

    const selected_user = useQuery({
        queryKey: ['getUserByEmail', user.email.toLowerCase()],
        queryFn: async () => {
            return await userAPI.getUserByEmail(user.email.toLowerCase());
        }
    });

    useEffect(() => {
        // A function that sets the mini state of the sidenav.
        function handleHiddenSidenav() {
            // setMiniSidenav(dispatch, window.innerWidth < 1200);
            setHiddenSidenav(dispatch, window.innerWidth < 1200);
            setMiniSidenav(dispatch, window.innerWidth < 1200);
            setDeviceStyle(dispatch, window.innerWidth < 1200 ? "mobile" : "desktop");
        }

        // The event listener that's calling the handleMiniSidenav function when resizing the window.
        window.addEventListener("resize", handleHiddenSidenav);

        // Call the handleMiniSidenav function to set the state with the initial value.
        handleHiddenSidenav();
        // setTransparentInfo(dispatch, true);

        // Remove event listener on cleanup
        return () => window.removeEventListener("resize", handleHiddenSidenav);
    }, [dispatch, location]);

    if (devices.isLoading || selected_user.isLoading) {
        return wrapper('Loading...');
    }

    if (devices.error || selected_user.error) {
        return wrapper("An error has occurred: " + devices.error ? devices.error.message : selected_user.error.message);
    }

    if (!devices.data || !selected_user.data) {
        return wrapper('Loading...');
    }

    var data = getGroupData(devices.data, getUserAccessLevel(selected_user.data.user.user_type, 'technician'));
    var position = { latitude: 0, longitude: 0 };

    try {
        var lat = devices.data.map((element) => Number(element.position.latitude));
        position.latitude = lat.reduce((x, y) => {
            return x + y;
        }, 0) / devices.data.length;

        var lng = devices.data.map((element) => Number(element.position.longitude));
        position.longitude = lng.reduce((x, y) => {
            return x + y;
        }, 0) / devices.data.length;

        if (isNaN(position.latitude) || isNaN(position.longitude)) {
            var position = { latitude: -33.8688, longitude: 151.2093 };
        }
    }
    catch (e) {
        console.log('improper device data provided');
    }

    var renderSidebarItems = (
        <SoftBox p={3}>
            {(sidebarState == 'sidenav' && deviceStyle == 'mobile') && (
                <IconButton
                    size="medium"
                    color="inherit"
                    // sx={navbarIconButton}
                    sx={{position: 'absolute', top: '0.5rem', right: '0.5rem'}}
                    onClick={closeSidenav}
                >
                    <Icon>close</Icon>
                </IconButton>
            )}
            <Grid container spacing={3} pb={1}>
                <Grid item xxs={12}>
                    <Grid container mt={2} sx={(theme) => sidebarHeadersStyle(theme, { sidebarState })}>
                        <Grid item xxs={12} textAlign={'center'}>
                            <SoftBox mb={3} className="userWelcome">
                                <SoftTypography>
                                    {timeOfDayMessage()} {user.nickname}!
                                </SoftTypography>
                            </SoftBox>
                        </Grid>
                        <Grid item xxs={12} textAlign={'center'}>
                            <SoftBox className="subheading">
                                <SoftTypography>
                                    What would you like to do today?
                                </SoftTypography>
                            </SoftBox>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xxs={12}>
                    <SidebarRoutes sidebarState={sidebarState} />
                </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={3} pt={1}>
                <Grid item xxs={12}>
                    <WeatherProvider position={position} icon_width={'50%'} />
                </Grid>
                {/* <Grid item xxs={12}>
                    <UptimeDoughnutChart
                        icon="percent"
                        color="secondary"
                        title="All Devices"
                        sidebarState={sidebarState}
                        devices={data.devices}
                    />
                </Grid> */}
            </Grid>
            {sidebarState == 'sidenav' && (
                <Grid container pt={2}>
                    <Grid item xxs={12}>
                        <SoftButton fullWidth onClick={(e) => { navigate('/') }}>Return to Menu</SoftButton>
                    </Grid>
                </Grid>
            )}
        </SoftBox>
    );

    if (sidebarState == 'sidenav') {
        return (
            <SoftBox sx={{ overflow: 'visible' }}>
                <SoftBox height="calc(100% - 48px)">
                    {/* <Grid container flexWrap={"nowrap"} sx={(theme) => customSidebarStyle(theme, { transparentInfo, transformX, sidebarState })}> */}
                    {/* <Grid item xxs={12} id="sidebar">sx={(theme) => sidebarTabStyle(theme, { transparentInfo, transformX, sidebarState })}> */}
                    <SidenavRoot variant="permanent" ownerState={{ transparentSidenav, miniSidenav, hiddenSidenav, sidebarState, deviceStyle }}>
                        {renderSidebarItems}
                    </SidenavRoot>
                    {/* </Grid> */}
                    {/* </Grid> */}
                </SoftBox>
            </SoftBox>
        );
    }
    else { //default
        return (
            <SoftBox sx={{ overflow: 'visible', height: '100%' }}>
                <Grid container sx={{ height: '100%' }}>
                    <Grid item xxs={12} sx={{ height: '100%' }}>
                        <Grid container flexWrap={"nowrap"} sx={(theme) => customSidebarStyle(theme, { transparentInfo, transformX, sidebarState })}>
                            <Grid item xxs={12} id="sidebar" sx={(theme) => sidebarTabStyle(theme, { transparentInfo, transformX, sidebarState })}>
                                {renderSidebarItems}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </SoftBox>
        );
    }
}

SideNav.propTypes = {
    sidebarOpen: PropTypes.bool,
    sidebarState: PropTypes.string,
}

export default SideNav;
